import {
	DefaultFabMenuItemActions,
	IContactSelection,
	ICreateCampaignRequest,
	IFabMenuItem,
	ILocationState,
	IPushNotificationMessage,
} from '@AppModels/.';
import { EventLogger, useEventLogging } from '@AppModels/Logging';
import {
	canSendHtmlNewsletters,
	canViewContentCalendarAndCampaigns,
	getMentionEntitiesFromLocationState,
} from '@AppModels/UiUtils';
import * as Api from '@ViewModels';
import { Fab } from '@WebComponents/Fab';
import { PushNotifications } from '@WebComponents/PushNotifications';
import { QuickAddEntity } from '@WebComponents/QuickAddEntity';
import { EmailReadOrOpenedToast } from '@WebComponents/Toaster/EmailReadOrOpenedToast';
import { ActionItemComposerModal } from '@WebComponents/actionItems/ActionItemComposer';
import { SingleEmailComposerModal } from '@WebComponents/email/SingleEmailComposerModal';
import { NoteComposerModal } from '@WebComponents/notes/NoteComposer';
import { CallIcon } from '@WebComponents/svgs/icons/CallIcon';
import { EmailIcon } from '@WebComponents/svgs/icons/EmailIcon';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, Switch, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { getDisplayName } from '../../../models/NameUtils';
import {
	useActionItemComposer,
	useEnvironment,
	useFullscreenModal,
	useNoteComposer,
	usePushNotifications,
	useQuickAddEntity,
	useSingleEmailComposer,
	useTextMessaging,
	useUserSession,
} from '../../../models/hooks/appStateHooks';
import {
	ActionItemViewModel,
	ContactViewModel,
	EntityViewModel,
	KnownCategories,
	NoteViewModel,
	OpportunityViewModel,
	RichContentComposerResult,
	UserSessionContext,
	VmUtils,
} from '../../../viewmodels/AppViewModels';
import { BrowserPushNotificationsViewModel } from '../../../viewmodels/PushNotifications';
import { AppLayout } from '../../components-v2/AppLayout/AppLayout';
import { Dialog, DialogPopup } from '../../components-v2/Dialog';
import { LevitateAppShell } from '../../components/AppShell';
import { EnablePushNotificationsModal } from '../../components/EnablePushNotificationsModal';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { IRouterLocation, PrivateRoute } from '../../components/PrivateRoute';
import { WebFullscreenModal } from '../../components/fullscreen/WebFullscreenModal';
import { ActionItemsIcon } from '../../components/svgs/icons/ActionItemsIcon';
import { NewContactIcon } from '../../components/svgs/icons/NewContactIcon';
import { ROUTES } from '../../constants/routes';
import { Contacts } from '../../pages/Contacts';
import { DataBoardsRoute } from '../../pages/DataBoards';
import { EditAutomationTemplateWithAppDrawer } from '../../pages/EditAutomations';
import { LazySurveysRoute } from '../../pages/Surveys/lazy';
import { AIResolver } from '../AIResolver';
import { AppLinks } from '../AppLinks';
import { Automations } from '../Automations';
import { BuildInfo } from '../BuildInfo';
import { Campaigns } from '../Campaigns';
import { Company } from '../Company';
import { Contact } from '../Contact';
import { ContactsImportFromCSVPage } from '../ContactsImportFromCSVPage';
import { CustomerContentCalendarPage } from '../ContentCalendar/CustomerContentCalendarPage';
import { CreateAutomationTemplatePage } from '../CreateAutomationTemplate';
import { EditHtmlNewsletterTemplate } from '../EditHtmlNewsletterTemplate';
import { GenericKeyDates } from '../GenericKeyDates';
import { Dashboard } from '../Happiness/Dashboard';
import { KeepInTouches } from '../KeepInTouches';
import { LazyLoader } from '../LazyLoader';
import { Logout } from '../Logout';
import { NotFoundContainer } from '../NotFoundContainer';
import { NoteActionItemsContainer } from '../NoteActionItemsContainer';
import { RecentMeetings } from '../RecentMeetings';
import { RecentlyViewedEmails } from '../RecentlyViewedEmails';
import { ScheduleMeetingSettingsPage } from '../ScheduleMeeting/ScheduleMeetingSettingsPage';
import { Settings } from '../Settings';
import { ManageUsers } from '../Settings/ManageUsers';
import { ManageUserGroups } from '../Settings/components/ManageUsersGroups';
import { TeamMeetingLinksPage } from '../TeamMeetingLinks';
import { TestimonialImageEditorContainer as TestimonialImageEditorContainerWrapper } from '../TestimonialImageEditor';
import { Texting } from '../Texting';
import { TextingRegistrationFormPage } from '../TextingRegistration/TextingRegistrationForm';
import { TextingRegistrationOptIn } from '../TextingRegistration/TextingRegistrationOptIn';
import { Integrations } from '../integrations/Integrations';
import './styles.less';

const LazyRegistrationRoute = React.lazy(() => import(/* webpackChunkName: "registration" */ '../TextingRegistration'));
const LazyReportingRoute = React.lazy(() => import(/* webpackChunkName: "reporting" */ '../reporting'));

export interface INavigationItemProps {
	routeContainerClassName?: string;
	showingCompactNav?: boolean;
}

export interface IMainContainerProps {
	children?: React.ReactElement<INavigationItemProps> | React.ReactElement<INavigationItemProps>[];
	className?: string;
	containerMode?: 'standard' | 'admin';
}

function _MainContainer({ containerMode = 'standard' }: IMainContainerProps) {
	const history = useHistory();
	const userSession = useUserSession();
	const loc = useLocation();
	const { logApiError } = useEventLogging('MainContainer');
	const fullscreenModal = useFullscreenModal();
	const environment = useEnvironment();
	const noteComposer = useNoteComposer();
	const actionItemComposer = useActionItemComposer();
	const singleEmailComposer = useSingleEmailComposer();
	const quickAddEntity = useQuickAddEntity();
	const pushNotifications = usePushNotifications();
	const textMessaging = useTextMessaging();

	const onEnablePushNotificationsModalRequestClose = async (enablePushNotifications?: boolean, cancel?: boolean) => {
		const logError = (error: any) => {
			const err = Api.asApiError(error);
			logApiError(`${enablePushNotifications ? 'Enable' : 'Disable'}BrowserPush-Error`, err);
		};
		if (cancel) {
			return pushNotifications.denyPushNotifications();
		}
		const resolvedValue = !!enablePushNotifications;
		await pushNotifications.toggleNotifications(resolvedValue).catch(logError);
	};

	const onStartComposingNote = (context?: Api.IRichContentContext) => () => {
		const locationState: ILocationState<EntityViewModel<Api.IEntity> | OpportunityViewModel, Api.IEntity> =
			fullscreenModal.hasValidLocation ? fullscreenModal.history.location.state : loc.state;
		const entities = getMentionEntitiesFromLocationState(locationState);
		const note: Api.INote = Api.createRichContentWithReferencedEntities(entities || []);
		note.context = context || null;

		// add "Phone call with..." to content, if needed
		if (
			!!note.context &&
			note.context.source === Api.RichContentContextSource.PhoneCall &&
			!!entities &&
			entities.length > 0
		) {
			const entityListStringValue = entities.reduce<string>((result, entityModel, i) => {
				const name = (getDisplayName(entityModel) || '').trim();
				if (name) {
					return `${result}${result ? (i === entities.length - 1 ? ' and ' : ', ') : ''}${name}`;
				}
				return result;
			}, '');
			if (entityListStringValue) {
				note.content = Api.createRawRichTextContentStateWithText(`Phone call with ${entityListStringValue}.`);
			}
		}

		EventLogger.logInput('Fab', 'CreateNote', 'Click');
		const noteVm = new NoteViewModel(userSession, note);
		noteComposer.show(noteVm);
	};

	const onEditActionItemCompleted = (actionItem?: ActionItemViewModel, result?: RichContentComposerResult) => {
		if (!!actionItem && result !== RichContentComposerResult.Delete && result !== RichContentComposerResult.None) {
			// action item was created... toast
			toast.success('Your action item was saved.', {
				action: {
					label: 'See Action Item',
					onClick: () => {
						history.push(`/notes-action-items?activeTab=action-items`);
					},
				},
			});
		}
	};

	const onFabMenuItemClicked = (menuItem: IFabMenuItem) => {
		if (menuItem.representedObject === DefaultFabMenuItemActions.AddPhoneCall) {
			EventLogger.logInput('Fab', 'AddPhoneCall', 'Click');
			onStartComposingNote({
				source: Api.RichContentContextSource.PhoneCall,
			})();
		} else if (menuItem.representedObject === DefaultFabMenuItemActions.CreateActionItem) {
			EventLogger.logInput('Fab', 'CreateActionItem', 'Click');
			const locationState: ILocationState<EntityViewModel<Api.IEntity> | OpportunityViewModel, Api.IEntity> =
				fullscreenModal.hasValidLocation ? fullscreenModal.history.location.state : loc.state;
			const entities = getMentionEntitiesFromLocationState(locationState);
			const actionItemModel: Api.IActionItem = Api.createRichContentWithReferencedEntities(entities || []);
			const actionItem = new ActionItemViewModel(userSession, actionItemModel);
			actionItemComposer.show(actionItem, onEditActionItemCompleted);
		} else if (menuItem.representedObject === DefaultFabMenuItemActions.SendMessage) {
			// try to get the contact vm from the current location state.
			const locationState: ILocationState<ContactViewModel, Api.IEntity> = fullscreenModal.hasValidLocation
				? fullscreenModal.history.location.state
				: loc.state;
			let contacts: ContactViewModel[] = (getMentionEntitiesFromLocationState(locationState) || [])
				.filter(x => Api.getTypeForEntity(x) !== 'company')
				.map(x => new ContactViewModel(userSession, x));
			if (
				(!contacts || contacts.length === 0) &&
				!!locationState &&
				!!locationState.model &&
				Api.getTypeForEntity(locationState.model) !== 'company'
			) {
				contacts = [new ContactViewModel(userSession, locationState.model)];
			}

			EventLogger.logInput(
				'Fab',
				'EmailComposer',
				'Click',
				contacts
					? {
							contacts: contacts.map(x => {
								return { id: x.id };
							}),
						}
					: undefined
			);
			if (!!contacts && contacts.length > 0) {
				singleEmailComposer.showForRecipients(contacts);
			} else {
				const selectionLocationState: ILocationState<any, ICreateCampaignRequest<IContactSelection>> = {
					model: {
						context: {
							contacts: contacts || [],
						},
						type: 'Contacts',
					},
				};
				fullscreenModal?.history?.push({
					pathname: '/email/campaigns/create/from-scratch',
					state: selectionLocationState,
				});
			}
		} else if (menuItem.representedObject === DefaultFabMenuItemActions.NewContact) {
			quickAddEntity.show({ type: 'contact' });
		}
	};

	const onRenderModals = () => {
		const showPushNotificationsPrompt = !!pushNotifications?.shouldShowPromptDialog;
		const fabMenuItems: IFabMenuItem<string>[] = [
			{
				icon: <EmailIcon />,
				representedObject: DefaultFabMenuItemActions.SendMessage,
				tooltip: 'Send an email',
			},
			{
				icon: <ActionItemsIcon />,
				representedObject: DefaultFabMenuItemActions.CreateActionItem,
				tooltip: 'Add Action Item',
			},
			{
				icon: <CallIcon />,
				representedObject: DefaultFabMenuItemActions.AddPhoneCall,
				tooltip: 'Add Phone Call',
			},
			{
				icon: <NewContactIcon />, // placeholder
				representedObject: DefaultFabMenuItemActions.NewContact,
				tooltip: 'New Contact',
			},
		];
		return (
			<React.Fragment key='main-container-modals'>
				<WebFullscreenModal />
				{containerMode !== 'admin' && (
					<Fab
						menuItems={fabMenuItems}
						onClick={onStartComposingNote()}
						onMenuItemClick={onFabMenuItemClicked}
						tooltip='Add Note'
					/>
				)}
				<ActionItemComposerModal />
				<NoteComposerModal />
				{environment.appType !== 'admin' && <SingleEmailComposerModal />}
				<Dialog
					open={quickAddEntity.isOpen}
					onOpenChange={open => {
						if (!open) {
							quickAddEntity.close();
						}
					}}
				>
					<DialogPopup>
						<QuickAddEntity
							quickAddEntityVm={quickAddEntity}
							onContactCreated={contact => history.push(`${ROUTES.contacts.people}/${contact.id}`)}
						/>
					</DialogPopup>
				</Dialog>
				{!!BrowserPushNotificationsViewModel.isSupported && (
					<EnablePushNotificationsModal
						modalProps={{
							isOpen: !!showPushNotificationsPrompt,
							onRequestClose: onEnablePushNotificationsModalRequestClose,
						}}
					/>
				)}
			</React.Fragment>
		);
	};

	const onPushNotificationMessageReceived = (e: MessageEvent) => {
		const pushNotificationMessage: IPushNotificationMessage = e.data;
		if (!!pushNotificationMessage && !!pushNotificationMessage.message && !!pushNotificationMessage.title) {
			toast(<EmailReadOrOpenedToast pushMessage={pushNotificationMessage} />, {
				duration: 5000,
			});
		}
	};

	/** Enforce permissions, handle special cases, etc. */
	const onRouteLoad = (routerLocation: IRouterLocation, _userSession: UserSessionContext) => {
		const lowerPathName = routerLocation.pathname?.toLocaleLowerCase();
		if (_userSession?.userRole === 'limitedUser') {
			switch (lowerPathName) {
				case '/companies': {
					return '/dashboard';
				}
				case '/automations': {
					return '/dashboard';
				}
				default: {
					break;
				}
			}
		}
	};

	return (
		<ErrorBoundary>
			<Switch>
				<PrivateRoute
					path='/launch'
					pageViewLogName={location.pathname}
					render={props => (
						<LevitateAppShell>
							<AppLinks {...props} />
						</LevitateAppShell>
					)}
					userSession={userSession}
				/>
				<PrivateRoute path='/build-info' pageViewLogName='build-info' userSession={userSession}>
					<LevitateAppShell>
						<BuildInfo routeContainerClassName='main-container-child' />
					</LevitateAppShell>
				</PrivateRoute>
				<PrivateRoute
					path='/logout'
					pageViewLogName='logout'
					render={props => (
						<LevitateAppShell>
							<Logout {...props} userSession={userSession} textMessaging={textMessaging} />
						</LevitateAppShell>
					)}
					userSession={userSession}
				/>
				<PrivateRoute
					path='/dashboard/recently-viewed-emails'
					pageViewLogName='dashboard/recently-viewed-emails'
					userSession={userSession}
				>
					<LevitateAppShell>
						<RecentlyViewedEmails />
					</LevitateAppShell>
				</PrivateRoute>
				<PrivateRoute
					path='/dashboard/recent-meetings'
					pageViewLogName='dashboard/recent-meetings'
					userSession={userSession}
				>
					<LevitateAppShell>
						<RecentMeetings />
					</LevitateAppShell>
				</PrivateRoute>
				<PrivateRoute
					path='/dashboard/keep-in-touches'
					pageViewLogName='dashboard/keep-in-touches'
					userSession={userSession}
				>
					<LevitateAppShell>
						<KeepInTouches />
					</LevitateAppShell>
				</PrivateRoute>
				<PrivateRoute
					path='/dashboard/upcoming-key-dates'
					pageViewLogName='dashboard/upcoming-key-dates'
					userSession={userSession}
				>
					<LevitateAppShell>
						<GenericKeyDates />
					</LevitateAppShell>
				</PrivateRoute>
				<PrivateRoute path='/dashboard' pageViewLogName='dashboard' userSession={userSession}>
					<AppLayout>
						<Dashboard />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/content-calendar' pageViewLogName='content-calendar' userSession={userSession}>
					<AppLayout>
						<CustomerContentCalendarPage />
					</AppLayout>
				</PrivateRoute>
				{userSession?.account?.features?.aida?.enabled && (
					<PrivateRoute path='/templates' userSession={userSession}>
						<LevitateAppShell>
							<Redirect
								exact={true}
								from='/templates'
								to={`/campaigns/email?category=${encodeURIComponent(KnownCategories.MyTemplates)}`}
							/>
						</LevitateAppShell>
					</PrivateRoute>
				)}
				{canSendHtmlNewsletters(userSession) ? (
					<PrivateRoute path='/campaigns/htmlnewsletter/:id' userSession={userSession}>
						<LevitateAppShell>
							<EditHtmlNewsletterTemplate routeContainerClassName='main-container-child' />
						</LevitateAppShell>
					</PrivateRoute>
				) : null}
				{canSendHtmlNewsletters(userSession) ? (
					<Redirect exact={true} from='/campaigns/htmlnewsletter' to={`/campaigns/newsletters`} />
				) : null}
				{canViewContentCalendarAndCampaigns(userSession) && (
					<PrivateRoute path='/campaigns/:tab?' userSession={userSession}>
						<AppLayout>
							<Campaigns routeContainerClassName='main-container-child' />
						</AppLayout>
					</PrivateRoute>
				)}
				<PrivateRoute path={`${ROUTES.contacts.people}/import`} userSession={userSession}>
					<LevitateAppShell>
						<ContactsImportFromCSVPage />
					</LevitateAppShell>
				</PrivateRoute>
				<Redirect from='/people/:id' to={`${ROUTES.contacts.people}/:id`} />
				<Redirect from='/companies/:id' to={`${ROUTES.contacts.companies}/:id`} />
				<PrivateRoute path={`${ROUTES.contacts.people}/:id`} pageViewLogName='contact/view' userSession={userSession}>
					<AppLayout>
						<Contact />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute
					path={`${ROUTES.contacts.companies}/:id`}
					pageViewLogName='company/view'
					userSession={userSession}
				>
					<AppLayout>
						<Company />
					</AppLayout>
				</PrivateRoute>
				<Redirect from='/people' to={ROUTES.contacts.people} />
				<Redirect from='/companies' to={ROUTES.contacts.companies} />
				<PrivateRoute path={ROUTES.contacts.main} pageViewLogName='people' userSession={userSession}>
					<AppLayout>
						<Contacts />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/notes-action-items/:id?' pageViewLogName='notes-action-items' userSession={userSession}>
					<AppLayout>
						<NoteActionItemsContainer />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute
					path='/texting/registration/form'
					pageViewLogName='campaign-registration-form'
					userSession={userSession}
					onLoad={onRouteLoad}
				>
					<LevitateAppShell>
						<TextingRegistrationFormPage />
					</LevitateAppShell>
				</PrivateRoute>
				{userSession?.account?.features?.texting?.enabled ? (
					<PrivateRoute
						path='/texting/registration/opt-in'
						pageViewLogName='campaign-registration-opt-in'
						userSession={userSession}
						onLoad={onRouteLoad}
					>
						<LevitateAppShell>
							<TextingRegistrationOptIn />
						</LevitateAppShell>
					</PrivateRoute>
				) : null}
				<PrivateRoute path='/texting/registration' pageViewLogName='campaign-registration' userSession={userSession}>
					<LevitateAppShell>
						<LazyLoader>
							<LazyRegistrationRoute />
						</LazyLoader>
					</LevitateAppShell>
				</PrivateRoute>
				<PrivateRoute path='/reporting' userSession={userSession}>
					<AppLayout>
						<LazyLoader>
							<LazyReportingRoute />
						</LazyLoader>
					</AppLayout>
				</PrivateRoute>
				<Redirect from='/settings/schedule-meeting' to='/schedule-meeting' />
				<PrivateRoute
					path='/schedule-meeting/team-links'
					pageViewLogName='settings/schedule-meeting/team-links'
					userSession={userSession}
				>
					<AppLayout>
						<TeamMeetingLinksPage />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/schedule-meeting' pageViewLogName='settings/schedule-meeting' userSession={userSession}>
					<AppLayout>
						<ScheduleMeetingSettingsPage />
					</AppLayout>
				</PrivateRoute>
				<Redirect from='/settings/manage-users' to='/settings/admin/manage-users' />
				<PrivateRoute path='/settings/admin/manage-users' userSession={userSession}>
					<LevitateAppShell>
						<ManageUsers routeContainerClassName='main-container-child' />
					</LevitateAppShell>
				</PrivateRoute>
				<PrivateRoute path='/settings/admin/manage-groups' userSession={userSession}>
					<LevitateAppShell>
						<ManageUserGroups routeContainerClassName='main-container-child' />
					</LevitateAppShell>
				</PrivateRoute>
				<PrivateRoute path='/settings/:category?/:action?' pageViewLogName='settings' userSession={userSession}>
					<AppLayout>
						<Settings />
					</AppLayout>
				</PrivateRoute>
				<Redirect exact={true} from='/boards/opportunities' to='/dataBoards/opportunities' />
				<PrivateRoute path='/dataBoards' userSession={userSession}>
					<AppLayout>
						<LazyLoader>
							<DataBoardsRoute />
						</LazyLoader>
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/integrations' pageViewLogName='integrations' userSession={userSession}>
					<AppLayout>
						<Integrations />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute
					path='/testimonial-image-editor/:id?'
					pageViewLogName='testimonial-image-editor'
					userSession={userSession}
				>
					<TestimonialImageEditorContainerWrapper />
				</PrivateRoute>
				<PrivateRoute path='/automations/new' pageViewLogName='create-automation-template' userSession={userSession}>
					<LevitateAppShell>
						<CreateAutomationTemplatePage routeContainerClassName='main-container-child' />
					</LevitateAppShell>
				</PrivateRoute>
				<PrivateRoute path='/automations/:id' pageViewLogName='edit-automation-template' userSession={userSession}>
					<AppLayout>
						<EditAutomationTemplateWithAppDrawer />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/automations' pageViewLogName='automations' userSession={userSession}>
					<AppLayout>
						<Automations />
					</AppLayout>
				</PrivateRoute>
				{userSession.account.features?.surveys?.enabled ? (
					<PrivateRoute path='/surveys' pageViewLogName='surveys' userSession={userSession}>
						<AppLayout>
							<LazySurveysRoute />
						</AppLayout>
					</PrivateRoute>
				) : (
					<Redirect from='/surveys' to='/dashboard' />
				)}
				{VmUtils.canViewTexting(userSession) ? (
					<PrivateRoute path='/texting' pageViewLogName='texting' exact={true} userSession={userSession}>
						<AppLayout>
							<Texting />
						</AppLayout>
					</PrivateRoute>
				) : null}
				{VmUtils.canViewTexting(userSession) ? (
					<PrivateRoute
						path='/texting/:phoneNumberId/:conversationId?'
						pageViewLogName='texting'
						userSession={userSession}
					>
						<AppLayout>
							<Texting />
						</AppLayout>
					</PrivateRoute>
				) : null}
				<PrivateRoute
					path='/airesolver/:entity/:id/:parentEntity?/:parentId?'
					pageViewLogName='airesolver'
					userSession={userSession}
				>
					<LevitateAppShell>
						<AIResolver />
					</LevitateAppShell>
				</PrivateRoute>
				<PrivateRoute userSession={userSession}>
					<LevitateAppShell>
						<NotFoundContainer routeContainerClassName='main-container-child' />
					</LevitateAppShell>
				</PrivateRoute>
			</Switch>
			{onRenderModals()}
			<PushNotifications onMessage={onPushNotificationMessageReceived} />
		</ErrorBoundary>
	);
}

export const MainContainer = observer(_MainContainer);
